import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";

// ==========================================
// 📦 STATIC IMPORTS (Layouts & Middleware)
// ==========================================
import AdminLayout from "./components/layout/AdminLayout";
import ProtectedRoute from "./middleware/ProtectedRoute";

// Global CSS (Toastify stays global, Quill is removed from here)
import "react-toastify/dist/ReactToastify.css";
import AllRequestLeads from "./pages/leads/AllRequestLeads.jsx";
import RequestLeads from "./pages/leads/RequestLeads.jsx";

// ==========================================
// 🚀 LAZY LOADED COMPONENTS (Code Splitting)
// ==========================================

// Auth & Core Public Pages
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const OTP = lazy(() => import("./pages/auth/OTP"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const LeadForm = lazy(() => import("./pages/forms/LeadForm"));
const ViewInvoice = lazy(() => import("./pages/invoices/ViewInvoice"));
const EmployeeInvite = lazy(() => import("./pages/employees/InviteEmployee"));

// Dashboard & Analytics
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const TeamAnalysis = lazy(() => import("./pages/data/TeamAnalysis.jsx"));
const Calendar = lazy(() => import("../src/pages/calendar/Calendar"));

// Leads Management
const AllLeads = lazy(() => import("./pages/leads/AllLeads"));
const WonLeads = lazy(() => import("./pages/leads/WonLeads"));
const LostLeads = lazy(() => import("./pages/leads/LostLeads.jsx"));
const TodayLeads = lazy(() => import("./pages/leads/TodayLeads"));
const FollowupLeads = lazy(() => import("./pages/leads/FollowupLeads"));
const AllFollowupLeads = lazy(() => import("./pages/leads/AllFollowupLeads"));
const UnallocatedLeads = lazy(() =>
  import("./pages/leads/UnallocatedLeads.jsx")
);
const LeadsTool = lazy(() => import("./pages/leads/LeadsTool.jsx"));
const CreateLead = lazy(() => import("./pages/leads/CreateLead"));
const UpdateLead = lazy(() => import("./pages/leads/UpdateLead"));
const LeadDetails = lazy(() => import("./pages/leads/LeadDetails"));

// Pipelines / Lead Types
const PipelineList = lazy(() => import("./pages/leads/PipelineList"));
const PipelineCreate = lazy(() => import("./pages/leads/PipelineCreate"));
const PipelineUpdate = lazy(() => import("./pages/leads/PipelineUpdate"));

// Employees
const EmployeeList = lazy(() => import("./pages/employees/EmployeeList"));
const CreateEmployee = lazy(() => import("./pages/employees/CreateEmployee"));
const UpdateEmployee = lazy(() => import("./pages/employees/UpdateEmployee"));

// Banks & Taxes
const BanksList = lazy(() => import("./pages/banks/BanksList"));
const CreateBank = lazy(() => import("./pages/banks/CreateBank"));
const UpdateBank = lazy(() => import("./pages/banks/UpdateBank"));
const TaxesList = lazy(() => import("./pages/taxes/TaxesList"));
const CreateTax = lazy(() => import("./pages/taxes/CreateTax"));
const UpdateTax = lazy(() => import("./pages/taxes/UpdateTax"));

// Invoices & Transactions
const InvoicesList = lazy(() => import("./pages/invoices/InvoicesList"));
const InvoiceWithTaxList = lazy(() =>
  import("./pages/invoices/InvoiceWithTaxList")
);
const InvoiceWithoutTaxList = lazy(() =>
  import("./pages/invoices/InvoiceWithoutTaxList")
);
const CreateInvoice = lazy(() => import("./pages/invoices/CreateInvoice"));
const UpdateInvoice = lazy(() => import("./pages/invoices/UpdateInvoice"));
const TransactionsList = lazy(() =>
  import("./pages/transactions/TransactionsList")
);
const CreateTransaction = lazy(() =>
  import("./pages/transactions/CreateTransaction")
);
const UpdateTransaction = lazy(() =>
  import("./pages/transactions/UpdateTransaction")
);

// Subscriptions
const SubscriptionsList = lazy(() =>
  import("./pages/subscriptions/SubscriptionsList")
);
const CreateSubscription = lazy(() =>
  import("./pages/subscriptions/CreateSubscription")
);
const UpdateSubscription = lazy(() =>
  import("./pages/subscriptions/UpdateSubscription")
);

// Reports
const ReportsList = lazy(() => import("./pages/reports/ReportsList.jsx"));
const CreateReportTemplate = lazy(() =>
  import("./pages/reports/CreateReportTemplate")
);
const ReportRuntimeCanvas = lazy(() =>
  import("./pages/reports/ReportRuntimeCanvas")
);

// Customers
const CustomersList = lazy(() => import("./pages/customers/CustomersList"));
const CreateCustomer = lazy(() => import("./pages/customers/CreateCustomer"));
const UpdateCustomer = lazy(() => import("./pages/customers/UpdateCustomer"));
const CustomerDetails = lazy(() => import("./pages/customers/CustomerDetails"));
const UnallocatedCustomers = lazy(() =>
  import("./pages/customers/UnallocatedCustomers.jsx")
);

// Devices
const DevicesList = lazy(() => import("./pages/devices/DevicesList"));
const UpdateDevice = lazy(() => import("./pages/devices/UpdateDevice"));

// Miscellaneous / Settings
const Setting = lazy(() => import("./pages/others/Setting"));
const Profile = lazy(() => import("./pages/others/Profile"));
const Notifications = lazy(() => import("./pages/notifications/Notifications"));
const NotFound = lazy(() => import("./pages/others/NotFound"));

export default function App() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-gray-50 text-gray-500 font-medium">
          Loading system...
        </div>
      }
    >
      <Routes>
        {/* ========== Public Routes ========== */}
        <Route index element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-otp" element={<OTP />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/form/lead-form/:leadId/:leadToken"
          element={<LeadForm />}
        />
        <Route path="/invoice/view" element={<ViewInvoice />} />
        <Route path="/invite" element={<EmployeeInvite />} />

        {/* ========== Protected Routes ========== */}
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<AdminLayout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="/data/team-analysis" element={<TeamAnalysis />} />
            <Route path="calendars" element={<Calendar />} />

            {/* Leads */}
            <Route path="/lead/today-followups" element={<FollowupLeads />} />
            <Route path="/lead/followups" element={<FollowupLeads />} />
            <Route path="/lead/all-followups" element={<AllFollowupLeads />} />
            <Route path="/lead/won" element={<WonLeads />} />
            <Route path="/lead/lost" element={<LostLeads />} />
            <Route path="/lead/today" element={<TodayLeads />} />
            <Route path="/lead/all" element={<AllLeads />} />
            <Route path="/lead/unallocated" element={<UnallocatedLeads />} />
            <Route path="/lead/request" element={<RequestLeads />} />
            <Route path="/lead/all-request" element={<AllRequestLeads />} />
            <Route path="/lead/tool" element={<LeadsTool />} />
            <Route path="/lead/live-status" element={<AllLeads />} />
            <Route path="/lead/create" element={<CreateLead />} />
            <Route path="/lead/update" element={<UpdateLead />} />

            {/* Pipelines / Lead Types */}
            <Route path="lead-types" element={<PipelineList />} />
            <Route path="lead-type/create" element={<PipelineCreate />} />
            <Route path="lead-type/update" element={<PipelineUpdate />} />
            <Route path="lead/details" element={<LeadDetails />} />

            {/* Employees */}
            <Route path="employees" element={<EmployeeList />} />
            <Route path="employee/create" element={<CreateEmployee />} />
            <Route path="employee/update" element={<UpdateEmployee />} />

            {/* Banks */}
            <Route path="banks" element={<BanksList />} />
            <Route path="bank/create" element={<CreateBank />} />
            <Route path="bank/update" element={<UpdateBank />} />

            {/* Taxes */}
            <Route path="taxes" element={<TaxesList />} />
            <Route path="tax/create" element={<CreateTax />} />
            <Route path="tax/update" element={<UpdateTax />} />

            {/* Invoices */}
            <Route path="invoices" element={<InvoicesList />} />
            <Route
              path="invoices/with-tax"
              element={<InvoiceWithTaxList />}
            />
            <Route
              path="invoices/without-tax"
              element={<InvoiceWithoutTaxList />}
            />
            <Route path="invoice/create" element={<CreateInvoice />} />
            <Route path="invoice/update" element={<UpdateInvoice />} />

            {/* Subscriptions */}
            <Route path="subscriptions" element={<SubscriptionsList />} />
            <Route
              path="subscription/create"
              element={<CreateSubscription />}
            />
            <Route
              path="subscription/update"
              element={<UpdateSubscription />}
            />

            {/* Transactions */}
            <Route path="transactions" element={<TransactionsList />} />
            <Route path="transaction/create" element={<CreateTransaction />} />
            <Route path="transaction/update" element={<UpdateTransaction />} />

            {/* Reports */}
            <Route path="reports" element={<ReportsList />} />
            <Route path="report/create" element={<CreateReportTemplate />} />
            <Route path="report/canvas" element={<ReportRuntimeCanvas />} />

            {/* Customers */}
            <Route path="customers" element={<CustomersList />} />
            <Route path="customer/create" element={<CreateCustomer />} />
            <Route path="customer/update" element={<UpdateCustomer />} />
            <Route path="customer/details" element={<CustomerDetails />} />
            <Route
              path="customer/unallocated"
              element={<UnallocatedCustomers />}
            />

            {/* Devices */}
            <Route path="devices" element={<DevicesList />} />
            <Route path="device/update" element={<UpdateDevice />} />

            {/* Others */}
            <Route path="settings" element={<Setting />} />
            <Route path="profile" element={<Profile />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>
        </Route>

        {/* ========== 404 Fallback ========== */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
